import type { ApiResponse } from '@halo/web-types';

let gatewayUrl = 'http://localhost:3001';
let unauthorizedCallback: (() => void) | null = null;
let getAccessToken: (() => string | null) | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let refreshInFlight: Promise<string | null> | null = null;

type ErrorToaster = (code: string, message: string) => void;
let errorToaster: ErrorToaster | null = null;

/** Register a toast handler for impersonation-related API errors. */
export function configureErrorToaster(fn: ErrorToaster): void {
  errorToaster = fn;
}

/** Call once at app startup to configure the API gateway URL. */
export function configureApi(baseUrl: string): void {
  gatewayUrl = baseUrl;
}

/** Return the configured gateway URL (used by AuthProvider for refresh calls). */
export function getGatewayUrl(): string {
  return gatewayUrl;
}

/** Register a token provider so the API client can read the in-memory access token. */
export function configureTokenProvider(provider: () => string | null): void {
  getAccessToken = provider;
}

/** Register a callback for 401 responses (called by AuthProvider). */
export function onUnauthorized(cb: () => void): void {
  unauthorizedCallback = cb;
}

/**
 * Register a refresh callback so 401 responses can attempt to refresh the
 * access token before logging the user out. The handler should hit the
 * refresh endpoint, update auth state, and return the new token (or null
 * if refresh failed).
 */
export function configureRefreshHandler(handler: () => Promise<string | null>): void {
  refreshHandler = handler;
}

/**
 * Single-flight wrapper: if many requests 401 at the same time, only one
 * refresh call fires. All callers await the same promise.
 */
async function tryRefreshOnce(): Promise<string | null> {
  if (!refreshHandler) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshHandler()
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Generate a unique request ID for correlation between frontend and backend.
 * Uses crypto.randomUUID when available, falls back to a timestamp + random hex.
 */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  const hex = Math.random().toString(16).slice(2, 10);
  return `${Date.now().toString(36)}-${hex}`;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAccessToken?.() ?? null;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const storeId = localStorage.getItem('halo_store_id');
  if (storeId) {
    headers['X-Store-Id'] = storeId;
  }

  return headers;
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Extra headers merged over the defaults (e.g. an explicit X-Store-Id override). */
  headers?: Record<string, string>;
}

/**
 * Try to parse the response body as JSON. Returns undefined when the body
 * cannot be parsed (e.g. empty 204 or HTML error pages from a proxy).
 */
async function safeJson<T>(res: Response): Promise<ApiResponse<T> | undefined> {
  try {
    return (await res.json()) as ApiResponse<T>;
  } catch {
    return undefined;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  isRetryAfterRefresh = false,
): Promise<ApiResponse<T>> {
  const url = `${gatewayUrl}${path}`;
  const requestId = generateRequestId();

  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      'X-Request-Id': requestId,
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  // ── 401 — session expired (or bad credentials on auth endpoints) ───────────
  if (res.status === 401) {
    const isAuthEndpoint =
      path === '/api/v1/auth/login' ||
      path === '/api/v1/auth/register' ||
      path === '/api/v1/auth/refresh' ||
      path === '/api/v1/auth/logout';

    // Surface the actual backend error (e.g. "Invalid credentials") when present.
    // Falls back to the generic message for protected-route 401s without a body.
    const json = await safeJson<T>(res);

    // Handle impersonation expiry — let the app-level toaster decide the redirect URL.
    if (json?.code === 'impersonation_expired' && errorToaster) {
      errorToaster('impersonation_expired', 'Your impersonation session has expired.');
      return {
        ...json,
        success: false,
        requestId: json.requestId ?? requestId,
      } as ApiResponse<T>;
    }

    // Attempt to refresh the access token before giving up. During deploys or
    // natural expiry, a stale access token can 401 once; the refresh token in
    // the httpOnly cookie is still valid and a single refresh call rescues the
    // session without logging the user out. Only retry once (flag guarded).
    if (!isAuthEndpoint && !isRetryAfterRefresh) {
      const newToken = await tryRefreshOnce();
      if (newToken) {
        return request<T>(path, options, true);
      }
    }

    if (!isAuthEndpoint && unauthorizedCallback) {
      unauthorizedCallback();
    }

    if (json) {
      return {
        ...json,
        success: false,
        requestId: json.requestId ?? requestId,
      };
    }

    return {
      success: false,
      error: 'Session expired',
      code: 'TOKEN_EXPIRED',
      requestId,
    } as ApiResponse<T>;
  }

  // ── 403 — insufficient permissions ─────────────────────────────────────────
  if (res.status === 403) {
    return {
      success: false,
      error: 'You do not have permission to perform this action.',
      code: 'INSUFFICIENT_PERMISSIONS',
      requestId,
    } as ApiResponse<T>;
  }

  // ── 429 — rate limited ─────────────────────────────────────────────────────
  if (res.status === 429) {
    const retryAfter = res.headers.get('Retry-After');
    return {
      success: false,
      error: retryAfter
        ? `Too many requests. Try again in ${retryAfter} seconds.`
        : 'Too many requests. Please slow down.',
      code: 'RATE_LIMIT_EXCEEDED',
      requestId,
    } as ApiResponse<T>;
  }

  // ── 503 — service unavailable ──────────────────────────────────────────────
  if (res.status === 503) {
    return {
      success: false,
      error: 'This feature is temporarily unavailable. Please try again shortly.',
      code: 'SERVICE_UNAVAILABLE',
      requestId,
    } as ApiResponse<T>;
  }

  // ── 500+ — server error ────────────────────────────────────────────────────
  if (res.status >= 500) {
    const json = await safeJson<T>(res);
    if (json) {
      // Backend may include code, requestId, and details in the body
      return {
        ...json,
        success: false,
        requestId: json.requestId ?? requestId,
      };
    }
    return {
      success: false,
      error: 'An unexpected server error occurred. Please try again later.',
      code: 'SERVER_ERROR',
      requestId,
    } as ApiResponse<T>;
  }

  // ── Success or 4xx with a JSON body ────────────────────────────────────────
  const json = await safeJson<T>(res);
  if (!json) {
    return {
      success: false,
      error: 'Invalid response from server.',
      code: 'INVALID_RESPONSE',
      requestId,
    } as ApiResponse<T>;
  }

  // ── Impersonation error toasts (403/4xx bodies from the deny-list) ─────────
  if (!json.success && json.code && errorToaster) {
    if (json.code === 'action_blocked_during_impersonation') {
      errorToaster(
        json.code,
        'This action is blocked during impersonation. Exit impersonation and use the admin panel.',
      );
    } else if (json.code === 'write_mode_disabled') {
      errorToaster(
        json.code,
        'This action requires write mode. Enable write mode in the banner above.',
      );
    }
    // impersonation_expired is handled above in the 401 branch (redirect, no toast).
  }

  // Attach requestId to every response for traceability
  return {
    ...json,
    requestId: json.requestId ?? requestId,
  };
}

export const api = {
  get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'GET',
      signal: options?.signal,
      headers: options?.headers,
    });
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  },

  delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  },
};

/** Re-export ApiError so consumers can import from the same module. */
export { ApiError, isApiError } from './api-error.js';
